import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { actualizarComprobante } from '../services/api';
import { useAuthValidation } from '../hooks/useAuthValidation';
import { formatDateToDDMMYYYY } from '../utils/dateUtils';

const EditComprobanteForm = () => {
  useAuthValidation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const comprobante = location.state?.comprobante;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre_razon_social: comprobante?.nombre_razon_social || '',
    tipo_comprobante: comprobante?.tipo_comprobante || '109',
    numero_comprobante: comprobante?.numero_comprobante || '',
    monto_gravado_10: comprobante?.monto_gravado_10 ?? 0,
    monto_gravado_5: comprobante?.monto_gravado_5 ?? 0,
    monto_exento: comprobante?.monto_exento ?? 0,
    monto_total: comprobante?.monto_total ?? 0,
    imputa_iva: comprobante?.imputa_iva || 'S',
    imputa_ire: comprobante?.imputa_ire || 'N',
    imputa_irp_rsp: comprobante?.imputa_irp_rsp || 'S',
  });

  if (!comprobante) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center text-muted">
          <p>No se encontraron datos del comprobante.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/comprobantes')}>
            Volver al listado
          </button>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'nombre_razon_social') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
      return;
    }

    if (name === 'numero_comprobante') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      if (cleaned.length > 3) formatted = cleaned.slice(0, 3) + '-' + cleaned.slice(3);
      if (cleaned.length > 6) formatted = cleaned.slice(0, 3) + '-' + cleaned.slice(3, 6) + '-' + cleaned.slice(6, 13);
      setFormData(prev => ({ ...prev, [name]: formatted }));
      return;
    }

    if (['monto_gravado_10', 'monto_gravado_5', 'monto_exento'].includes(name)) {
      setFormData(prev => {
        const g10 = name === 'monto_gravado_10' ? parseInt(value) || 0 : parseInt(prev.monto_gravado_10) || 0;
        const g5  = name === 'monto_gravado_5'  ? parseInt(value) || 0 : parseInt(prev.monto_gravado_5)  || 0;
        const ext = name === 'monto_exento'     ? parseInt(value) || 0 : parseInt(prev.monto_exento)     || 0;
        return { ...prev, [name]: value, monto_total: g10 + g5 + ext };
      });
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await actualizarComprobante(id, {
        ...formData,
        monto_gravado_10: parseInt(formData.monto_gravado_10) || 0,
        monto_gravado_5:  parseInt(formData.monto_gravado_5)  || 0,
        monto_exento:     parseInt(formData.monto_exento)     || 0,
        monto_total:      parseInt(formData.monto_total)      || 0,
      });
      toast.success('Comprobante actualizado correctamente');
      navigate('/comprobantes');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al actualizar comprobante');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="comprobante-form">
      <div className="card shadow-sm">
        <div className="card-header bg-warning text-dark">
          <h4 className="mb-0">
            <i className="bi bi-pencil-square"></i> Editar Comprobante
          </h4>
        </div>
        <div className="card-body">
          {/* Read-only identity fields */}
          <div className="alert alert-info mb-4">
            <div className="row">
              <div className="col-md-4">
                <strong>RUC:</strong> {comprobante.identificador}
              </div>
              <div className="col-md-4">
                <strong>Fecha:</strong> {formatDateToDDMMYYYY(comprobante.fecha_comprobante)}
              </div>
              <div className="col-md-4">
                <strong>Timbrado:</strong> {comprobante.nro_timbrado}
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Razón Social */}
            <div className="row mb-3">
              <div className="col-12">
                <label className="form-label">Razón Social *</label>
                <input
                  type="text"
                  className="form-control"
                  name="nombre_razon_social"
                  value={formData.nombre_razon_social}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Tipo y Número de comprobante */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">Tipo de Comprobante *</label>
                <select
                  className="form-control"
                  name="tipo_comprobante"
                  value={formData.tipo_comprobante}
                  onChange={handleChange}
                  required
                >
                  <option value="109">109 - FACTURA</option>
                  <option value="110">110 - NOTA DE CRÉDITO</option>
                  <option value="111">111 - NOTA DE DÉBITO</option>
                  <option value="103">103 - BOLETA DE VENTA</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Número de Comprobante *</label>
                <input
                  type="text"
                  className="form-control"
                  name="numero_comprobante"
                  value={formData.numero_comprobante}
                  onChange={handleChange}
                  placeholder="001-001-0000001"
                  required
                />
              </div>
            </div>

            {/* Montos */}
            <div className="row mb-3">
              <div className="col-md-3">
                <label className="form-label">Monto Gravado 10%</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_gravado_10"
                  value={formData.monto_gravado_10}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Monto Gravado 5%</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_gravado_5"
                  value={formData.monto_gravado_5}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Monto Exento</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_exento"
                  value={formData.monto_exento}
                  onChange={handleChange}
                  min="0"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Monto Total</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_total"
                  value={formData.monto_total}
                  readOnly
                  style={{ backgroundColor: '#e9ecef' }}
                />
              </div>
            </div>

            {/* Flags */}
            <div className="row mb-4">
              <div className="col-md-4">
                <label className="form-label">Imputa IVA</label>
                <select className="form-control" name="imputa_iva" value={formData.imputa_iva} onChange={handleChange}>
                  <option value="S">Sí</option>
                  <option value="N">No</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Imputa IRE</label>
                <select className="form-control" name="imputa_ire" value={formData.imputa_ire} onChange={handleChange}>
                  <option value="S">Sí</option>
                  <option value="N">No</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Imputa IRP/RSP</label>
                <select className="form-control" name="imputa_irp_rsp" value={formData.imputa_irp_rsp} onChange={handleChange}>
                  <option value="S">Sí</option>
                  <option value="N">No</option>
                </select>
              </div>
            </div>

            {/* Actions */}
            <div className="row">
              <div className="col-12">
                <button type="submit" className="btn btn-warning me-2" disabled={loading}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-1" />Guardando...</>
                    : <><i className="bi bi-save"></i> Guardar Cambios</>}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate('/comprobantes')}>
                  <i className="bi bi-x-circle"></i> Cancelar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditComprobanteForm;
